/** Мелкие общие блоки раздела «Грузоместа»: пометка происхождения, статус, время. */
import React from "react";

import type { CargoOrigin, CargoStatus } from "@/api/boxes";
import { cn } from "@/lib/utils";

/**
 * Откуда грузоместо — небольшой пометкой. Складу всё равно, откуда оно, но
 * действия разные: коробку склада делят и печатают, короб с упаковки — снимают
 * с паллеты и вскрывают.
 */
export function OriginBadge({ origin }: { origin: CargoOrigin }): React.ReactElement {
  const packing = origin === "packing";
  return (
    <span
      className={cn(
        "shrink-0 rounded px-1.5 py-0.5 text-[11px] font-medium",
        packing ? "bg-violet-100 text-violet-700" : "bg-slate-100 text-slate-600",
      )}
    >
      {packing ? "с упаковки" : "склад"}
    </span>
  );
}

const STATUS_CLS: Record<CargoStatus, string> = {
  whole: "bg-emerald-100 text-emerald-700",
  opened: "bg-amber-100 text-amber-800",
  empty: "bg-slate-200 text-slate-600",
  voided: "bg-rose-100 text-rose-700",
  dissolved: "bg-slate-200 text-slate-600",
};

/** Статус словами — текст с бэка (`status_text`), цвет — по коду статуса. */
export function CargoStatusChip({
  status,
  text,
  className,
}: {
  status: CargoStatus;
  text: string;
  className?: string;
}): React.ReactElement {
  return (
    <span
      className={cn(
        "rounded px-2 py-0.5 text-xs",
        STATUS_CLS[status] ?? "bg-slate-100 text-slate-600",
        className,
      )}
    >
      {text}
    </span>
  );
}

/** «1 короб», «3 короба», «5 коробов». */
export function boxesWord(n: number): string {
  const tail = n % 100;
  const last = n % 10;
  const word =
    tail >= 11 && tail <= 14
      ? "коробов"
      : last === 1
        ? "короб"
        : last >= 2 && last <= 4
          ? "короба"
          : "коробов";
  return `${n} ${word}`;
}

/** Дата и время по местному времени устройства: «06.10.2026 13:15». */
export function fmtWhen(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}
